// lib/services/animation/animation_controller_gpu.ts

import AnimationLibrary from "./animation_library";
import AnimationClip from "./animation_clip";
import CharacterBuilderGpu from "../compute/character_builder_gpu";
import AnimationLibraryPool from "../compute/animation_library_pool";
import AnimationPoseSampler from "./animation_pose_sampler";
import AnimationPool from "../compute/animation_pool";
import AnimationSlot, { ANIMATION_STATE_BYTES } from "../compute/animation_slot";
import AnimationComputePipeline from "../compute/pipelines/animation_compute_pipeline";

export type SequenceStep = {
  clipIndex: number;
  blendInSeconds: number;
  loop: boolean;
};

type Slot = {
  clip: AnimationClip;
  clipIndex: number;
  time: number;
  loop: boolean;
};



export default class AnimationControllerGpu {
  readonly key: string;

  private library: AnimationLibrary;
  private builder: CharacterBuilderGpu;
  private library_pool: AnimationLibraryPool;
  private animation_pool: AnimationPool;
  private pipeline: AnimationComputePipeline;

  private current: Slot | null = null;
  private next: Slot | null = null;
  private blendDuration = 0;
  private immediateBlendStartTime: number | null = null;
  private queue: SequenceStep[] = [];

  private state_scratch: ArrayBuffer = new ArrayBuffer(ANIMATION_STATE_BYTES);
  private state_view_u32: Uint32Array;
  private state_view_f32: Float32Array;

  // Currently-leased render-side animation slot, held between
  // acquire_latest_joint_palette() and release_latest_joint_palette().
  private leased_render_slot: AnimationSlot | null = null;
  readonly rootMotionDelta = {
      translation: new Float32Array([0, 0, 0]),
      rotationY: 0,
    };

  constructor(
    key: string,
    library: AnimationLibrary,
    builder: CharacterBuilderGpu,
    library_pool: AnimationLibraryPool,
    animation_pool: AnimationPool,
    pipeline: AnimationComputePipeline
  ) {
    this.key = key;
    this.library = library;
    this.builder = builder;
    this.library_pool = library_pool;
    this.animation_pool = animation_pool;
    this.pipeline = pipeline;

    this.state_view_u32 = new Uint32Array(this.state_scratch);
    this.state_view_f32 = new Float32Array(this.state_scratch);
  }

  // --- Public clip-control API ---------------------------------------------

  playLoopByIndex(clipIndex: number): void {
    const clip = this.library.getByIndex(clipIndex);
    this.current = { clip, clipIndex, time: 0,loop:true };
    this.next = null;
    this.blendDuration = 0;
    this.immediateBlendStartTime = null;
    this.queue = [];
  }

  playSequence(steps: SequenceStep[]): void {
  if (steps.length === 0) return;

  const first = steps[0];
  const firstClip = this.library.getByIndex(first.clipIndex);

  if (this.current) {
    this.next = { clip: firstClip, clipIndex: first.clipIndex, time: 0, loop: first.loop };
    this.blendDuration = Math.max(0.0001, first.blendInSeconds);
    this.immediateBlendStartTime = performance.now() / 1000;
  } else {
    this.current = { clip: firstClip, clipIndex: first.clipIndex, time: 0, loop: first.loop };
    this.next = null;
    this.blendDuration = 0;
    this.immediateBlendStartTime = null;
  }

  this.queue = steps.slice(1);
}

  // --- RenderObject lifecycle hooks ----------------------------------------

  /**
   * CPU-only state advance. Called from scene_service.tick.
   */
  tick(deltaTime: number): void {
    if (!this.current) return;

    this.maybeScheduleNextStep();
    this.advanceTimes(deltaTime);
    this.extractRootMotion(deltaTime);
    this.finalizeTransition();
  }

  /**
   * Encodes the compute dispatch for this frame into the shared compute pass.
   * Called from render_service inside the compute phase.
   */
  encode_compute(device: GPUDevice, pass: GPUComputePassEncoder): void {
    if (!this.current) return;

    const library_slot = this.library_pool.acquire_latest_ready_slot();
    if (!library_slot) {
      return;
    }

    const animation_slot = this.animation_pool.acquire_idle_slot();
    if (!animation_slot || !animation_slot.begin_record()) {
      this.library_pool.release_latest_ready_slot();
      return;
    }

    if (!animation_slot.animation_state_buffer) {
      this.animation_pool.cancel_slot(animation_slot);
      this.library_pool.release_latest_ready_slot();
      return;
    }

    this.writeAnimationStateBuffer(device, animation_slot);

    this.pipeline.prepare(library_slot, animation_slot);
 
    this.pipeline.dispatch(pass);

    this.animation_pool.publish_slot(animation_slot, performance.now() / 1000);
    this.library_pool.release_latest_ready_slot();
  }

  /**
   * Encodes any readback copies into the frame's encoder. No-op until
   * AnimationCpuPool is wired in.
   */
  readback_encode(device: GPUDevice, encoder: GPUCommandEncoder): void {
    // To be implemented when AnimationCpuPool is wired in.
  }

  /**
   * Drives any mapAsync promises to completion. No-op until
   * AnimationCpuPool is wired in.
   */
  readback_poll(): void {
    // To be implemented when AnimationCpuPool is wired in.
  }

  // --- Consumer-side palette lease for the render path --------------------

  acquire_latest_joint_palette(): GPUBuffer | null {
    this.leased_render_slot = this.animation_pool.acquire_latest_ready_slot();
    if (!this.leased_render_slot) return null;
    return this.leased_render_slot.joint_palette_buffer;
  }

  release_latest_joint_palette(): void {
    if (this.leased_render_slot) {
      this.animation_pool.release_latest_ready_slot();
      this.leased_render_slot = null;
    }
  }

  // --- Internal: clip-time scheduling --------------------------------------

  private maybeScheduleNextStep(): void {
  if (this.next) return;
  if (this.queue.length === 0) return;
  if (!this.current) return;

  const peek = this.queue[0];
  const blend = Math.max(0.0001, peek.blendInSeconds);
  const remaining = this.current.clip.duration - this.current.time;

  if (remaining <= blend || this.current.loop) {
    this.queue.shift();
    const clip = this.library.getByIndex(peek.clipIndex);
    this.next = { clip, clipIndex: peek.clipIndex, time: 0, loop: peek.loop };
    this.blendDuration = blend;

    if (this.current.loop) {
      this.immediateBlendStartTime = performance.now() / 1000;
    } else {
      this.immediateBlendStartTime = null;
    }
  }
}

private extractRootMotion(deltaTime: number): void {
  this.rootMotionDelta.translation[0] = 0;
  this.rootMotionDelta.translation[1] = 0;
  this.rootMotionDelta.translation[2] = 0;
  this.rootMotionDelta.rotationY = 0;

  if (!this.current) return;

  const alpha = this.next ? this.computeBlendAlpha() : 0;
  const source = alpha < 0.5 || !this.next ? this.current : this.next;

  const clip = source.clip;
  const prevTime = Math.max(0, source.time - deltaTime);
  const currTime = source.time;

  const rootNode = clip.rootMotionNodeIndex;
  if (rootNode < 0) return;

  const translation = this.sampleClipNodeTranslation(clip, rootNode, prevTime, currTime, source.loop);
  if (translation) {
    this.rootMotionDelta.translation[0] = translation[0];
    this.rootMotionDelta.translation[1] = translation[1];
    this.rootMotionDelta.translation[2] = translation[2];

  }

  this.rootMotionDelta.rotationY = this.sampleClipNodeRotationY(clip, rootNode, prevTime, currTime, source.loop);
}

private sampleClipNodeTranslation(
  clip: AnimationClip,
  nodeIndex: number,
  prevTime: number,
  currTime: number,
  loop: boolean
): Float32Array | null {
  const channel = clip.channels.find(
    (c) => c.nodeIndex === nodeIndex && c.path === "translation"
  );
  if (!channel) return null;

  const sampleRaw = (time: number) =>
    AnimationPoseSampler.sampleChannel(channel, time, clip.duration, false);

  const sub = (a: Float32Array, b: Float32Array) =>
    new Float32Array([b[0] - a[0], b[1] - a[1], b[2] - a[2]]);

  const add = (a: Float32Array, b: Float32Array) =>
    new Float32Array([a[0] + b[0], a[1] + b[1], a[2] + b[2]]);

  if (!loop || currTime >= prevTime) {
    const a = sampleRaw(prevTime);
    const b = sampleRaw(currTime);
    if (!a || !b) return null;
    return sub(a, b);
  }

  const a = sampleRaw(prevTime);
  const end = sampleRaw(clip.duration);
  const start = sampleRaw(0);
  const b = sampleRaw(currTime);
  if (!a || !end || !start || !b) return null;

  return add(sub(a, end), sub(start, b));
}

private sampleClipNodeRotationY(
  clip: AnimationClip,
  nodeIndex: number,
  prevTime: number,
  currTime: number,
  loop: boolean
): number {
  const channel = clip.channels.find(
    (c) => c.nodeIndex === nodeIndex && c.path === "rotation"
  );
  if (!channel) return 0;

  const sampleRaw = (time: number) =>
    AnimationPoseSampler.sampleChannel(channel, time, clip.duration, false);

  const quatToYaw = (q: Float32Array): number => {
    const x = q[0], y = q[1], z = q[2], w = q[3];
    return Math.atan2(2 * (w * y + x * z), 1 - 2 * (y * y + z * z));
  };

  const wrapPi = (a: number): number => {
    while (a > Math.PI) a -= Math.PI * 2;
    while (a < -Math.PI) a += Math.PI * 2;
    return a;
  };

  if (!loop || currTime >= prevTime) {
    const a = sampleRaw(prevTime);
    const b = sampleRaw(currTime);
    if (!a || !b) return 0;
    return wrapPi(quatToYaw(b) - quatToYaw(a));
  }

  const a = sampleRaw(prevTime);
  const end = sampleRaw(clip.duration);
  const start = sampleRaw(0);
  const b = sampleRaw(currTime);
  if (!a || !end || !start || !b) return 0;

  const p1 = wrapPi(quatToYaw(end) - quatToYaw(a));
  const p2 = wrapPi(quatToYaw(b) - quatToYaw(start));
  return wrapPi(p1 + p2);
}

  private advanceTimes(deltaTime: number): void {
    if (!this.current) return;

    this.current.time = this.advanceTime(
      this.current.time,
      this.current.clip.duration,
      deltaTime,
      this.current.loop
    );

    if (this.next) {
      this.next.time = this.advanceTime(
        this.next.time,
        this.next.clip.duration,
        deltaTime,
        this.next.loop
      );
    }
  }

  private advanceTime(
    time: number,
    duration: number,
    deltaTime: number,
    loop: boolean
  ): number {
    let out = time + deltaTime;
    if (loop && duration > 0) {
      out = out % duration;
      if (out < 0) out += duration;
    } else {
      out = Math.min(out, duration);
    }
    return out;
  }

  private computeBlendAlpha(): number {
    if (!this.next) return 0;
    if (this.blendDuration <= 0) return 1;
    if (!this.current) return 0;

    if (this.immediateBlendStartTime !== null) {
      const elapsed = performance.now() / 1000 - this.immediateBlendStartTime;
      return Math.max(0, Math.min(1, elapsed / this.blendDuration));
    }

    const blendStartTime = Math.max(
      0,
      this.current.clip.duration - this.blendDuration
    );
    const elapsed = this.current.time - blendStartTime;
    return Math.max(0, Math.min(1, elapsed / this.blendDuration));
  }

  private finalizeTransition(): void {
    if (!this.current) return;
    if (!this.next) return;

    const alpha = this.computeBlendAlpha();
    const immediateBlendDone =
      this.immediateBlendStartTime !== null && alpha >= 1;
    const naturalEndReached =
      this.current.time >= this.current.clip.duration &&
      !this.current.loop;

    if (immediateBlendDone || naturalEndReached) {
      this.current = this.next;
      this.next = null;
      this.blendDuration = 0;
      this.immediateBlendStartTime = null;
    }
  }
  
  private writeAnimationStateBuffer(
  device: GPUDevice,
  animation_slot: AnimationSlot
): void {
  if (!animation_slot.animation_state_buffer) return;
  if (!this.current) return;

  const currentClipIndex = this.current.clipIndex;
  const currentTime = this.current.time;
  const nextClipIndex = this.next ? this.next.clipIndex : currentClipIndex;
  const nextTime = this.next ? this.next.time : 0;
  const blendAlpha = this.next ? this.computeBlendAlpha() : 0;
  const hasNext = this.next ? 1 : 0;
  const currentLoop = this.current.loop ? 1 : 0;
  const nextLoop = this.next ? (this.next.loop ? 1 : 0) : 0;

  this.state_view_u32[0] = currentClipIndex;
  this.state_view_u32[1] = nextClipIndex;
  this.state_view_f32[2] = currentTime;
  this.state_view_f32[3] = nextTime;
  this.state_view_f32[4] = blendAlpha;
  this.state_view_u32[5] = hasNext;
  this.state_view_u32[6] = currentLoop;
  this.state_view_u32[7] = nextLoop;

  device.queue.writeBuffer(
    animation_slot.animation_state_buffer,
    0,
    this.state_scratch
  );
}

get_internal_state(): {
  current: { clip: AnimationClip; time: number; loop: boolean } | null;
  next: { clip: AnimationClip; time: number; loop: boolean } | null;
  blendAlpha: number;
} {
  return {
    current: this.current
      ? { clip: this.current.clip, time: this.current.time, loop: this.current.loop }
      : null,
    next: this.next
      ? { clip: this.next.clip, time: this.next.time, loop: this.next.loop }
      : null,
    blendAlpha: this.next ? this.computeBlendAlpha() : 0,
  };
}

  get_state() {
    const active = this.next ?? this.current;

    return {
      clipIndex: active ? active.clipIndex : null,
      clipName: active ? active.clip.name : null,
      progress: active
        ? Math.max(
            0,
            Math.min(1, active.time / Math.max(0.0001, active.clip.duration))
          )
        : null,
      time: active ? active.time : null,
      duration: active ? active.clip.duration : null,
      isOneShotActive: this.next !== null,
      currentState: this.current
        ? {
            clipIndex: this.current.clipIndex,
            clipName: this.current.clip.name,
            time: this.current.time,
            progress:
              this.current.clip.duration > 0
                ? Math.max(
                    0,
                    Math.min(1, this.current.time / this.current.clip.duration)
                  )
                : 0,
          }
        : null,
      nextState: this.next
        ? {
            clipIndex: this.next.clipIndex,
            clipName: this.next.clip.name,
            time: this.next.time,
            progress:
              this.next.clip.duration > 0
                ? Math.max(
                    0,
                    Math.min(1, this.next.time / this.next.clip.duration)
                  )
                : 0,
          }
        : null,
      blendState:
        this.next && this.current
          ? {
              fromClipIndex: this.current.clipIndex,
              toClipIndex: this.next.clipIndex,
              time:
                this.current.time -
                Math.max(0, this.current.clip.duration - this.blendDuration),
              duration: this.blendDuration,
              progress: this.computeBlendAlpha(),
            }
          : null,
      queuedStepCount: this.queue.length,
    };
  }
}